import dayjs from "dayjs";
import { type GuestsInputType, type PaymentState } from "src/Modules/Hotels/constants";
import {
  type HotelInitiateRequest,
  type CustomerDetail,
  type CustomerName,
  type HotelBookingCustomerTitle,
  type HotelBookingGuestType,
  HOTEL_BOOKING_PAYMENT_MODES,
  HOTEL_BOOKING_CUSTOMER_TITLES,
  HOTEL_BOOKING_GUEST_TYPES,
} from "src/store/slices/hotels/Hotels";
import { type Hotel, type HotelRoom } from "src/store/slices/hotels/Hotels";
import { type SearchFormInputs } from "src/Modules/Hotels/constants/SearchPage";

type TransformGuestsResult = {
  customerDetails: CustomerDetail[];
  emailId: string;
  phoneNumber: string;
};

function transformGuests(
  guestsState: GuestsInputType,
  primaryGuestKey: string | null,
  rooms: { adults: number; children: number; childrenAges: number[] }[],
): TransformGuestsResult {
  const roomGroups: CustomerName[][] = Array.from({ length: rooms.length }, () => []);
  let emailId = "";
  let phoneNumber = "";

  const keys = Object.keys(guestsState);
  const parsed: { roomIndex: number; isAdult: boolean; key: string }[] = [];

  for (const key of keys) {
    const parts = key.split("_");
    if (parts.length < 4) continue;
    const roomIndex = parseInt(parts[1], 10);
    if (isNaN(roomIndex)) continue;
    parsed.push({ roomIndex, isAdult: parts[2] === "adult", key });
  }

  parsed.sort((a, b) => {
    if (a.roomIndex !== b.roomIndex) return a.roomIndex - b.roomIndex;
    if (a.isAdult !== b.isAdult) return a.isAdult ? -1 : 1;
    return 0;
  });

  for (const { key, roomIndex, isAdult } of parsed) {
    const guest = guestsState[key];
    if (!guest) continue;

    const type: HotelBookingGuestType = isAdult ? HOTEL_BOOKING_GUEST_TYPES.ADULT : HOTEL_BOOKING_GUEST_TYPES.CHILD;
    const title: HotelBookingCustomerTitle = guest.title ?? HOTEL_BOOKING_CUSTOMER_TITLES.MR;

    roomGroups[roomIndex].push({
      title,
      firstName: guest.firstName ?? "",
      lastName: guest.lastName ?? "",
      type,
    });

    if (key === primaryGuestKey && guest.contact) {
      emailId = guest.contact.email ?? "";
      phoneNumber = guest.contact.phone ?? "";
    }
  }

  return {
    customerDetails: roomGroups.map((customerNames) => ({ customerNames })),
    emailId,
    phoneNumber,
  };
}

function buildInitiateRequest(params: {
  guestsState: GuestsInputType;
  primaryGuestKey: string | null;
  hotel: Hotel;
  selectedRoom: HotelRoom | null;
  searchObject: SearchFormInputs;
  sessionId: string;
  overrideTotalFare?: number;
  paymentState?: PaymentState;
  pointsFactor: number;
  amountInCash:number;
  returnUrl: string;
}): HotelInitiateRequest {
  const { guestsState, primaryGuestKey, hotel, selectedRoom, searchObject, sessionId, overrideTotalFare, paymentState, amountInCash, returnUrl } =
    params;

  const { customerDetails, emailId, phoneNumber } = transformGuests(guestsState, primaryGuestKey, searchObject.rooms);

  const totalFare = overrideTotalFare ?? selectedRoom?.totalFare ?? 0;

  const checkInDate = searchObject.checkin ? dayjs(searchObject.checkin).locale("en").format("YYYY-MM-DD") : null;
  const checkIn = searchObject.checkin ? dayjs(searchObject.checkin).locale("en").format("YYYY-MM-DD") : "";
  const checkOut = searchObject.checkout ? dayjs(searchObject.checkout).locale("en").format("YYYY-MM-DD") : "";

  return {
    sessionId,
    bookingCode: selectedRoom?.bookingCode ?? "",
    cityCode: hotel.cityCode ?? "",
    hotelCode: hotel.hotelCode ?? "",
    hotelName: hotel.hotelName ?? "",
    cityName: hotel.cityName ?? "",
    roomCount: searchObject.rooms.length,
    amount: amountInCash,
    totalPrice: totalFare,
    paymentMode: HOTEL_BOOKING_PAYMENT_MODES.LIMIT,
    searchTotalFare: totalFare,
    customerDetails,
    clientReferenceId: crypto.randomUUID(),
    bookingReferenceId: crypto.randomUUID(),
    emailId,
    phoneNumber,
    checkInDate,
    checkIn,
    checkOut,
    returnUrl,
  };
}

export { transformGuests, buildInitiateRequest };

// ─── Price-change / booking-error modal flow engine ───────────────────────────

import { ReactNode } from "react";
import {ExclamationIcon, WarningIcon} from "src/assets/Icons";
import i18n from "src/i18n";

export interface FlowContext {
  navigateStep: (step: number) => void;
  navigate: ReturnType<(typeof import("react-router-dom"))["useNavigate"]>;
  searchId: string;
  reBook: () => void;
  refetchDetails: () => void;
  getFare: () => { oldFare: number; newFare: number };
}

export interface BookingFlags {
  isPriceChanged?: boolean;
  bookingFailed?: boolean;
}

interface ButtonConfig {
  title: string;
  onClick: (ctx: FlowContext) => void;
  className?: string;
}

interface FlowDefinition {
  value: string;
  keys: Partial<Record<keyof BookingFlags, boolean>>;
  failReason: ReactNode;
  suggestion: ReactNode;
  className?: string;
  icon: ReactNode;
  info?: (ctx: FlowContext) => ReactNode;
  buttons: ButtonConfig[];
}

export interface ResolvedFlow {
  failReason: ReactNode;
  suggestion: ReactNode;
  className?: string;
  icon: ReactNode;
  buttons: Array<{
    title: string;
    onClick: () => void;
    className?: string;
  }>;
  info?: ReactNode;
}

function createFlow(def: FlowDefinition) {
  return {
    value: def.value,
    keys: def.keys,
    resolve: (ctx: FlowContext): ResolvedFlow => ({
      failReason: def.failReason,
      suggestion: <div>{def.suggestion}</div>,
      className: def.className,
      icon: def.icon,
      info: def.info?.(ctx),
      buttons: def.buttons.map(({ title, onClick, className }) => ({
        title,
        className,
        onClick: () => onClick(ctx),
      })),
    }),
  };
}

const BASE_ICON_CLASS = "h-full w-full rounded-xl border-2 border-background-default p-3";

function getFlows() {
  return [
    createFlow({
      value: "2",
      keys: { bookingFailed: true },
      failReason: <span className="text-font-danger!">{i18n.t("Flights.details.payment.bookingFailed")}</span>,
      suggestion: <span className="text-font-subtle!">{i18n.t("Hotels.payment.bookingFailedSuggestion")}</span>,
      className: "bg-background-danger!",
      icon: <ExclamationIcon className={`${BASE_ICON_CLASS} bg-background-danger! border-none! text-font-danger!`} />,
      buttons: [
        {
          title: i18n.t("Flights.details.payment.tryBookingAgain"),
          onClick: ({ reBook }) => reBook(),
          className: "bg-background-default! font-semibold! text-font-danger",
        },
        {
          title: i18n.t("Hotels.payment.searchNewHotel"),
          onClick: ({ navigate, searchId }) => navigate(`/hotel/${searchId}?t=${Date.now()}`),
          className: "text-font-inverted! font-semibold! bg-background-inverted/20!",
        },
      ],
    }),
    createFlow({
      value: "1",
      keys: { isPriceChanged: true },
      failReason: i18n.t("Flights.details.payment.priceChanged"),
      suggestion: i18n.t("Hotels.payment.priceChangedSuggestion"),
      icon: <WarningIcon className={`${BASE_ICON_CLASS} bg-secondary-60 text-font-inverted`} />,
      info: (ctx) => {
        const { oldFare, newFare } = ctx.getFare();
        return (
          <p className="text-sm text-font-subtle">
            {i18n.t("Flights.details.payment.originalFare")} <span className="text-font-danger!">{oldFare}</span> → {i18n.t("Flights.details.payment.newFare")} <span>{newFare}</span>
          </p>
        );
      },
      buttons: [
        {
          title: i18n.t("Flights.details.payment.confirmPriceAndBook"),
          onClick: ({ reBook }) => reBook(),
          className: "text-font-inverted! bg-background-active! font-semibold!",
        },
        {
          title: i18n.t("Hotels.payment.searchNewHotels"),
          onClick: ({ navigate, searchId }) => navigate(`/hotel/${searchId}?t=${Date.now()}`),
          className: "bg-background-subtle! font-semibold!",
        },
      ],
    }),
  ];
}

export function resolveActiveFlow(flags: BookingFlags, ctx: FlowContext): ResolvedFlow | null {
  for (const flow of getFlows()) {
    const isMatch = (Object.entries(flow.keys) as [keyof BookingFlags, boolean][]).some(
      ([key, expectedValue]) => flags[key] === expectedValue,
    );
    if (isMatch) {
      return flow.resolve(ctx);
    }
  }

  return null;
}
